import React, { useState, useEffect, useRef } from "react";
import { Container } from "react-bootstrap";
import { Link } from "react-router-dom";
import my_logo from "../../assets/images/my_logo4.png";

// ── reCAPTCHA site key ────────────────────────────────────────────────────────
// Replace with your actual site key from https://www.google.com/recaptcha/admin
const RECAPTCHA_SITE_KEY =
  process.env.REACT_APP_RECAPTCHA_SITE_KEY || "YOUR_RECAPTCHA_SITE_KEY";

// ── Load reCAPTCHA script once ────────────────────────────────────────────────
function loadRecaptchaScript() {
  if (document.getElementById("recaptcha-script")) return;
  const script = document.createElement("script");
  script.id = "recaptcha-script";
  script.src = `https://www.google.com/recaptcha/api.js`;
  script.async = true;
  script.defer = true;
  document.head.appendChild(script);
}

// ── Subscribe Modal ──────────────────────────────────────────────────────────
function SubscribeModal({ onClose }) {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState(null); // null | "success" | "duplicate" | "error"
  const [loading, setLoading] = useState(false);
  const [captchaToken, setCaptchaToken] = useState(null);
  const [captchaError, setCaptchaError] = useState(false);
  const captchaRef = useRef(null);
  const widgetIdRef = useRef(null);

  // Load script and render widget when modal mounts
  useEffect(() => {
    loadRecaptchaScript();

    const renderWidget = () => {
      if (
        captchaRef.current &&
        window.grecaptcha &&
        widgetIdRef.current === null
      ) {
        widgetIdRef.current = window.grecaptcha.render(captchaRef.current, {
          sitekey: RECAPTCHA_SITE_KEY,
          callback: (token) => {
            setCaptchaToken(token);
            setCaptchaError(false);
          },
          "expired-callback": () => {
            setCaptchaToken(null);
          },
          "error-callback": () => {
            setCaptchaToken(null);
          },
        });
      }
    };

    // grecaptcha may already be ready, or we wait for it
    if (window.grecaptcha && window.grecaptcha.render) {
      renderWidget();
    } else {
      const interval = setInterval(() => {
        if (window.grecaptcha && window.grecaptcha.render) {
          clearInterval(interval);
          renderWidget();
        }
      }, 200);
      return () => clearInterval(interval);
    }
  }, []);

  async function handleSubmit(e) {
    e.preventDefault();
    if (!name.trim() || !email.trim()) return;

    if (!captchaToken) {
      setCaptchaError(true);
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/subscribe`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: name.trim(),
            email: email.trim(),
            captchaToken, // send token to your backend for server-side verification
          }),
        }
      );

      if (res.status === 201) {
        setStatus("success");
      } else if (res.status === 409) {
        setStatus("duplicate");
      } else {
        setStatus("error");
      }
    } catch (err) {
      console.error("Subscribe error:", err);
      setStatus("error");
    } finally {
      setLoading(false);
      // Reset captcha after submission attempt
      if (window.grecaptcha && widgetIdRef.current !== null) {
        window.grecaptcha.reset(widgetIdRef.current);
        setCaptchaToken(null);
      }
    }
  }

  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(0,0,0,0.45)",
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        zIndex: 9999,
        backdropFilter: "blur(2px)",
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: "12px",
          padding: "36px 32px",
          width: "100%",
          maxWidth: "420px",
          boxShadow: "0 20px 60px rgba(0,0,0,0.18)",
          position: "relative",
          fontFamily: "'Georgia', serif",
        }}
      >
        {/* Close */}
        <button
          onClick={onClose}
          style={{
            position: "absolute",
            top: "14px",
            right: "16px",
            background: "none",
            border: "none",
            fontSize: "20px",
            cursor: "pointer",
            color: "#9ca3af",
            lineHeight: 1,
          }}
          aria-label="Close"
        >
          ✕
        </button>

        {status === "success" ? (
          <div style={{ textAlign: "center", padding: "12px 0" }}>
            <div style={{ fontSize: "40px", marginBottom: "12px" }}>🎉</div>
            <h3
              style={{
                margin: "0 0 8px",
                fontSize: "20px",
                color: "#111",
                fontWeight: "700",
              }}
            >
              You're subscribed!
            </h3>
            <p style={{ color: "#6b7280", fontSize: "14px", margin: 0 }}>
              Welcome to BharatAbroad. We'll keep you updated with the latest
              stories.
            </p>
            <button
              onClick={onClose}
              style={{
                marginTop: "20px",
                padding: "10px 28px",
                background: "#f97316",
                color: "#fff",
                border: "none",
                borderRadius: "6px",
                fontSize: "14px",
                fontWeight: "600",
                cursor: "pointer",
              }}
            >
              Done
            </button>
          </div>
        ) : (
          <>
            <div style={{ marginBottom: "24px" }}>
              <p
                style={{
                  fontSize: "11px",
                  fontWeight: "700",
                  letterSpacing: "1.5px",
                  color: "#f97316",
                  textTransform: "uppercase",
                  margin: "0 0 6px",
                }}
              >
                Newsletter
              </p>
              <h2
                style={{
                  margin: "0 0 6px",
                  fontSize: "22px",
                  color: "#111",
                  fontWeight: "700",
                  lineHeight: 1.3,
                }}
              >
                Stay Connected with BharatAbroad
              </h2>
              <p style={{ margin: 0, fontSize: "13px", color: "#6b7280" }}>
                Get the latest news, stories &amp; updates delivered to your
                inbox.
              </p>
            </div>

            <form onSubmit={handleSubmit}>
              <div style={{ marginBottom: "14px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "5px",
                  }}
                >
                  Full Name
                </label>
                <input
                  type="text"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  placeholder="Arjun Sharma"
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1.5px solid #e5e7eb",
                    borderRadius: "7px",
                    fontSize: "14px",
                    outline: "none",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
              </div>

              <div style={{ marginBottom: "18px" }}>
                <label
                  style={{
                    display: "block",
                    fontSize: "12px",
                    fontWeight: "600",
                    color: "#374151",
                    marginBottom: "5px",
                  }}
                >
                  Email Address
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="arjun@email.com"
                  required
                  style={{
                    width: "100%",
                    padding: "10px 12px",
                    border: "1.5px solid #e5e7eb",
                    borderRadius: "7px",
                    fontSize: "14px",
                    outline: "none",
                    boxSizing: "border-box",
                    fontFamily: "inherit",
                  }}
                  onFocus={(e) => (e.target.style.borderColor = "#f97316")}
                  onBlur={(e) => (e.target.style.borderColor = "#e5e7eb")}
                />
              </div>

              {/* ── reCAPTCHA widget ── */}
              <div style={{ marginBottom: "16px" }}>
                <div ref={captchaRef} />
                {captchaError && (
                  <p
                    style={{
                      color: "#ef4444",
                      fontSize: "12px",
                      margin: "6px 0 0",
                    }}
                  >
                    Please complete the CAPTCHA to continue.
                  </p>
                )}
              </div>

              {status === "duplicate" && (
                <p
                  style={{
                    color: "#ef4444",
                    fontSize: "13px",
                    margin: "0 0 14px",
                  }}
                >
                  This email is already subscribed.
                </p>
              )}
              {status === "error" && (
                <p
                  style={{
                    color: "#ef4444",
                    fontSize: "13px",
                    margin: "0 0 14px",
                  }}
                >
                  Something went wrong. Please try again.
                </p>
              )}

              <button
                type="submit"
                disabled={loading}
                style={{
                  width: "100%",
                  padding: "12px",
                  background: loading ? "#fdba74" : "#f97316",
                  color: "#fff",
                  border: "none",
                  borderRadius: "7px",
                  fontSize: "14px",
                  fontWeight: "700",
                  cursor: loading ? "not-allowed" : "pointer",
                  letterSpacing: "0.3px",
                  transition: "background 0.2s",
                }}
                onMouseEnter={(e) => {
                  if (!loading) e.target.style.background = "#ea6c0a";
                }}
                onMouseLeave={(e) => {
                  if (!loading) e.target.style.background = "#f97316";
                }}
              >
                {loading ? "Subscribing…" : "Subscribe — It's Free"}
              </button>

              <p
                style={{
                  marginTop: "12px",
                  fontSize: "11px",
                  color: "#9ca3af",
                  textAlign: "center",
                }}
              >
                No spam, ever. Unsubscribe anytime.
              </p>
            </form>
          </>
        )}
      </div>
    </div>
  );
}

// ── TopNavbar ────────────────────────────────────────────────────────────────
function TopNavbar() {
  const [showSubscribe, setShowSubscribe] = useState(false);

  return (
    <>
      <div
        style={{
          backgroundColor: "#ffffff",
          borderBottom: "1px solid #e5e7eb",
        }}
      >
        <Container fluid className="px-4">
          {/* LINE 1: Logo + Social Icons + Subscribe */}
          <div
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              paddingTop: "8px",
              paddingBottom: "8px",
              borderBottom: "1px solid #f3f4f6",
              flexWrap: "wrap",
              gap: "12px",
            }}
          >
            <Link to="/home" style={{ textDecoration: "none" }}>
              <img
                src={my_logo}
                alt="BharatAbroad"
                style={{ height: "34px" }}
              />
            </Link>

            <div style={{ display: "flex", alignItems: "center", gap: "16px" }}>
              <a
                href="https://www.instagram.com/thebharatabroad/"
                target="_blank"
                rel="noreferrer"
                style={{ lineHeight: 0 }}
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/a/a5/Instagram_icon.png"
                  alt="Instagram"
                  style={{ height: "20px" }}
                />
              </a>
              <a
                href="https://www.youtube.com/@TheBharatAbroad"
                target="_blank"
                rel="noreferrer"
                style={{ lineHeight: 0 }}
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/b/b8/YouTube_play_button_icon_%282013%E2%80%932017%29.svg"
                  alt="YouTube"
                  style={{ height: "20px" }}
                />
              </a>
              <a
                href="/"
                target="_blank"
                rel="noreferrer"
                style={{ lineHeight: 0 }}
              >
                <img
                  src="https://upload.wikimedia.org/wikipedia/commons/c/ca/LinkedIn_logo_initials.png"
                  alt="LinkedIn"
                  style={{ height: "20px" }}
                />
              </a>

              {"\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0\u00A0"}

              <a
                href="https://chat.whatsapp.com/LNfGI3UFWbt69VeUbBoxLj"
                target="_blank"
                rel="noreferrer"
                style={{ lineHeight: 0 }}
              >
                <img
                  src="https://png.pngtree.com/element_our/md/20180626/md_5b321c98efaa6.jpg"
                  alt="WhatsApp"
                  style={{ height: "28px" }}
                />
              </a>

              <button
                onClick={() => setShowSubscribe(true)}
                style={{
                  padding: "6px 16px",
                  background: "#f97316",
                  color: "#fff",
                  border: "none",
                  borderRadius: "5px",
                  fontSize: "12px",
                  fontWeight: "600",
                  cursor: "pointer",
                  letterSpacing: "0.3px",
                  transition: "background 0.2s",
                  whiteSpace: "nowrap",
                }}
                onMouseEnter={(e) => (e.target.style.background = "#ea6c0a")}
                onMouseLeave={(e) => (e.target.style.background = "#f97316")}
              >
                Subscribe
              </button>
            </div>
          </div>

          {/* LINE 2: Navigation */}
          <div style={{ overflowX: "auto", whiteSpace: "nowrap" }}>
            <nav style={{ display: "inline-flex" }}>
              {[
                { to: "/", label: "Home" },
                { to: "/about-us", label: "About" },
                { to: "/sports", label: "Sports" },
                { to: "/health", label: "Health" },
                { to: "/technology", label: "Technology" },
                { to: "/auto", label: "Auto" },
                { to: "/entertainment", label: "Entertainment" },
                { to: "/immigration", label: "Immigration" },
                { to: "/heritage", label: "Heritage" },
                { to: "/travel", label: "Travel" },
              ].map((item) => (
                <Link
                  key={item.to}
                  to={item.to}
                  style={{
                    color: "#222",
                    fontSize: "13px",
                    fontWeight: "200",
                    paddingRight: "8px",
                    textDecoration: "none",
                    transition: "font-weight 0.01s ease",
                  }}
                  onMouseEnter={(e) => (e.target.style.fontWeight = "600")}
                  onMouseLeave={(e) => (e.target.style.fontWeight = "400")}
                >
                  {item.label}
                </Link>
              ))}
            </nav>
          </div>

          <hr style={{ margin: 0 }} />

          {/* LINE 3: Copyright + Policies */}
          <div
            style={{
              padding: "10px 0",
              display: "flex",
              flexWrap: "wrap",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <p style={{ margin: 0, fontSize: "12px", color: "#6b7280" }}>
              Copyright © {new Date().getFullYear()} BharatAbroad. All rights
              reserved. BharatAbroad is not responsible for the content of
              external sites.
            </p>
            <span style={{ color: "#9ca3af", fontSize: "12px" }}>•</span>
            <Link
              to="/privacy-policy"
              style={{
                fontSize: "12px",
                color: "#6b7280",
                textDecoration: "none",
                fontWeight: "400",
              }}
              onMouseEnter={(e) => (e.target.style.fontWeight = "600")}
              onMouseLeave={(e) => (e.target.style.fontWeight = "400")}
            >
              Privacy Policy
            </Link>
            <span style={{ color: "#9ca3af", fontSize: "12px" }}>•</span>
            <Link
              to="/Advertising-policy"
              style={{
                fontSize: "12px",
                color: "#6b7280",
                textDecoration: "none",
                fontWeight: "400",
              }}
              onMouseEnter={(e) => (e.target.style.fontWeight = "600")}
              onMouseLeave={(e) => (e.target.style.fontWeight = "400")}
            >
              Advertising Policy
            </Link>
          </div>
        </Container>
      </div>

      {showSubscribe && (
        <SubscribeModal onClose={() => setShowSubscribe(false)} />
      )}
    </>
  );
}

export default TopNavbar;
